import React, {FC, useEffect, useRef} from 'react';

import {
    sendDesktopAnalyticsEvent,
    sendDesktopAnalyticsPerSession,
} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Tooltip} from '@optimaxdev/design-system';
import {Affirm, AmazonPay, Icon} from '@optimaxdev/icons';
import classNames from 'classnames';
import {useInView} from 'react-intersection-observer';
import {connect} from 'react-redux';

import {useFeatureWithAnalytics} from 'components/feature';
import {CART_BUTTON, CART_CTA} from 'constants/analytics/events/cart';
import {getCartTotalPrice} from 'desktop/pages/cart/store/selectors';
import {AMAZON_BUTTON_ID_V2} from 'desktop/pages/checkout/constants/amazon';

import {ApplicationStoreType} from 'desktop/store';
import {addAmazonScript} from 'libs/amazon/amazonV2';
import {amazonButtonV2} from 'libs/amazon/button/buttonV2';
import {spawnPriorityTask} from 'libs/timers/spawnPriorityTask/spawnPriorityTask';
import {AmazonV2DataType} from 'types/amazonType';

import s from '../paymentsButtons.scss';

const AmazonOverlay: FC = () => (
    <div className={s.overlay}>
        <p>
            <b>Complete the process</b> with your
            <br />
            Amazon account and we will meet
            <br />
            again to <b>review your order</b>
        </p>
    </div>
);

/**
 * Send analytic when click on amazon button auth
 */
export function sendAnalyticsAmazonCartButton() {
    sendDesktopAnalyticsEvent(CART_CTA, {action: 'Right Section', label: 'Amazon pay'});
}

export type PropsType = {
    amazonV2: AmazonV2DataType | null;
    cartTotalPrice?: number;
    isBordered?: boolean;
};

/**
 * Displays amazon or amazonV2 payment systems under proceed to checkout button on react cart page
 * @params {PropsType} params
 * @returns {FC} amazon button component
 */
export const AmazonButton: FC<PropsType> = ({amazonV2, cartTotalPrice}) => {
    const amazonRef = useRef<HTMLDivElement | null>(null);
    const {inView, ref} = useInView();
    const isDCartAmazonCTA = useFeatureWithAnalytics('dCartUI_AmazonCTA', false, 'desktop');

    useEffect(() => {
        const isAmazonPayAvailable = amazonV2 && cartTotalPrice;

        if (isAmazonPayAvailable && amazonRef.current && !amazonRef.current.shadowRoot) {
            if (window.amazon) {
                amazonButtonV2({
                    color: 'Gold',
                    handleSendAnalytics: sendAnalyticsAmazonCartButton,
                    isSandbox: Boolean(Number(amazonV2.isSandbox)),
                    sellerId: amazonV2.sellerId,
                    total: cartTotalPrice,
                });
            } else {
                addAmazonScript(
                    amazonButtonV2.bind(null, {
                        color: 'Gold',
                        handleSendAnalytics: sendAnalyticsAmazonCartButton,
                        isSandbox: Boolean(Number(amazonV2.isSandbox)),
                        sellerId: amazonV2.sellerId,
                        total: cartTotalPrice,
                    }),
                );
            }
        }
    }, [amazonV2, cartTotalPrice]);

    useEffect(() => {
        if (inView) {
            spawnPriorityTask(() =>
                sendDesktopAnalyticsPerSession({
                    event: CART_BUTTON,
                    label: {action: 'Right Section', label: 'Visible - Amazon CTA'},
                }),
            );
        }
    }, [inView]);

    if (!amazonV2) return null;

    return (
        <div className={s.amazonCTA}>
            <Tooltip
                closeTrigger="hover"
                content={<AmazonOverlay />}
                openTrigger="hover"
                position="left"
                renderAs="div"
                size="small"
            >
                <div className={s.buttonWrapper} ref={ref}>
                    <button
                        className={classNames(
                            s.button,
                            s.amazonPay,
                            s.amazonPayV2,
                            s.buttonRedesign,
                        )}
                        data-test-name="cartSummaryAmazonButton"
                        type="button"
                    >
                        <Icon icon={AmazonPay} />
                    </button>
                    <div
                        className={s.amazonButton}
                        id={AMAZON_BUTTON_ID_V2}
                        ref={amazonRef}
                        role="presentation"
                    />
                </div>
            </Tooltip>
            {isDCartAmazonCTA && (
                <div className={s.creditSuggestion}>
                    <span className={s.marketingText}>Pay over time with</span>
                    <Icon height={25} icon={Affirm} width={42.6} />
                </div>
            )}
        </div>
    );
};

const mapStateToProps = (store: ApplicationStoreType) => ({
    cartTotalPrice: getCartTotalPrice(store),
});

export const AmazonButtonConnected = connect(mapStateToProps)(AmazonButton);
